/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {CacheScope, CacheTTL} from '@adobe/exc-app/cache';
import DataPrefetchContract from '@adobe/exc-app/network/DataPrefetchContract';
import {FetchScope} from '@adobe/exc-app/network';
import {Recents, RecentVariables} from '@exc/graphql/src/models/recents';
import {RECENTS_QUERY} from '@exc/graphql/src/queries/recents';

// This contract will eventually live in @exc/pueblo and will be consumed through a manifest.
// Currently placing it here.

export const DEFAULT_PAGE_SIZE = 20;
export const RECENTS_PREFETCH_KEY = 'recents-org';
export const RECENTS_SANDBOX_PREFETCH_KEY = 'recents-sandbox';

const RECENTS_VARIABLES: RecentVariables = {
  acrossSandbox: true,
  pageSize: DEFAULT_PAGE_SIZE
};

// Recents (Org level) Data Contract
export const recentsContract: DataPrefetchContract<Recents> = {
  defaultValue: {cursor: null, recents: []},
  expiry: CacheTTL.DAY,
  gql: {
    dataPath: 'recent',
    fetchScope: FetchScope.ORG,
    query: RECENTS_QUERY,
    variables: RECENTS_VARIABLES
  },
  isSensitive: false,
  key: RECENTS_PREFETCH_KEY,
  refreshData: {
    dedup: {
      arrayElement: 'recents',
      arrayLimit: 1000,
      keyField: 'objId',
      sortAscending: false,
      sortField: 'clickTimestamp'
    },
    gql: {
      dataPath: 'recent',
      fetchScope: FetchScope.ORG,
      query: RECENTS_QUERY,
      variables: {...RECENTS_VARIABLES, tillTimeStamp: '${dedup.sortField}'}
    },
    keepOriginal: ['cursor']
  },
  revalidate: {
    enabled: true,
    revalidateAfterSec: 30
  },
  scope: CacheScope.ORG,
  shared: true
};

// Recents (Sandbox level) Data Contract
export const recentsBySandboxContract: DataPrefetchContract<Recents> = {
  defaultValue: {cursor: null, recents: []},
  expiry: CacheTTL.DAY,
  gql: {
    dataPath: 'recent',
    fetchScope: FetchScope.SANDBOX,
    query: RECENTS_QUERY,
    variables: {...RECENTS_VARIABLES, acrossSandbox: false}
  },
  isSensitive: false,
  key: RECENTS_SANDBOX_PREFETCH_KEY,
  refreshData: {
    dedup: {
      arrayElement: 'recents',
      arrayLimit: 1000,
      keyField: 'objId',
      sortAscending: false,
      sortField: 'clickTimestamp'
    },
    gql: {
      dataPath: 'recent',
      fetchScope: FetchScope.SANDBOX,
      query: RECENTS_QUERY,
      variables: {...RECENTS_VARIABLES, acrossSandbox: false, tillTimeStamp: '${dedup.sortField}'}
    },
    keepOriginal: ['cursor']
  },
  revalidate: {
    enabled: true,
    revalidateAfterSec: 30
  },
  scope: CacheScope.SANDBOX,
  shared: true
};
